import { SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';

export type RadioButtonGroupInputChoices = {
  id: string;
  name: string;
  options?: Record<string, unknown>;
  subtitle?: string;
  disabled?: boolean;
}[];

export type RadioButtonGroupInputProps = {
  disabled?: boolean;
  defaultValue?: string;
  choices: RadioButtonGroupInputChoices;
  source: string;
  onChange?: (value: string) => unknown;
  label?: string | false;
  labelSx?: SxProps;
  inline?: boolean;
  required?: boolean;
  format?: (value: unknown) => string;
  'data-testid'?: string;
};

// react-admin RadioButtonGroupInput is broken
export function RadioButtonGroupInput({
  choices,
  defaultValue,
  disabled,
  label,
  onChange,
  source,
  labelSx = {},
  inline = false,
  required = false,
  format,
  'data-testid': dataTestId,
}: RadioButtonGroupInputProps) {
  const translate = useTranslate();
  const { getValues, setValue, control, clearErrors } = useFormContext();
  const value = useMemo<unknown>(() => getValues(source), [getValues, source]);

  useEffect(() => {
    if (value === undefined && defaultValue) {
      setValue(source, defaultValue);
    }
  }, [defaultValue, setValue, source, value]);

  return (
    <Controller
      name={source}
      control={control}
      rules={{ required: required ? translate('ra.validation.required') : false }}
      render={({ field, fieldState }) => {
        const containerStyle: SxProps = inline
          ? { flexDirection: 'row', alignItems: 'center' }
          : { flexDirection: 'column' };

        return (
          <Box sx={{ display: 'flex', ...containerStyle }}>
            {label !== false && label !== undefined && (
              <Typography sx={{ fontSize: '0.75em', marginBottom: '0.2em', ...labelSx }}>
                {translate(label)} {required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: inline ? 'row' : 'column' }}>
              {choices.map((choice) => (
                <Box
                  key={`RadioButtonGroupInput-${choice.id}`}
                  sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel
                    label={translate(choice.name, choice.options)}
                    control={
                      <Radio
                        color="primary"
                        disabled={disabled || choice.disabled}
                        checked={(format ? format(field.value) : field.value) === choice.id}
                        onChange={() => {
                          field.onChange(choice.id);
                          clearErrors(source);
                          if (onChange) {
                            onChange(choice.id);
                          }
                        }}
                        value={choice.id}
                        data-testid={dataTestId}
                      />
                    }
                  />
                  {choice.subtitle && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        marginLeft: '33px',
                        marginTop: '-6px',
                        color: 'DarkGray',
                      }}>
                      {translate(choice.subtitle, choice.options)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {fieldState.error && (
              <Typography sx={{ color: 'red', fontSize: '0.75rem', marginTop: '0.2em' }}>
                {fieldState.error.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
